import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { enumLabel, useI18n } from '../lib/i18n';
import { MONTH_NAMES, formatDate, formatTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

const TYPE_TONE: Record<string, 'brand' | 'plasma' | 'info' | 'neutral'> = {
  MORNING: 'brand',
  NIGHT: 'plasma',
  CUSTOM: 'info',
  OFF: 'neutral',
};

export function MySchedulePage() {
  const { t, locale } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);

  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const to = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-schedule', from, to],
    queryFn: () => api.get<any[]>('/my-schedule', { from, to }),
  });

  const shiftMonth = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  const todayString = now.toISOString().slice(0, 10);
  const working = data?.filter((d) => d.type !== 'OFF') ?? [];
  const nights = working.filter((d) => d.type === 'NIGHT').length;

  return (
    <>
      <PageHeader
        title={t('shifts.mySchedule')}
        action={
          <div className="flex items-center gap-1">
            <Button size="sm" icon={<ChevronLeft className="h-4 w-4 flip-rtl" />} onClick={() => shiftMonth(-1)} />
            <span className="min-w-[160px] text-center text-title text-[var(--text-primary)]">
              {MONTH_NAMES[locale][month - 1]} {year}
            </span>
            <Button size="sm" icon={<ChevronRight className="h-4 w-4 flip-rtl" />} onClick={() => shiftMonth(1)} />
          </div>
        }
      />

      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-caption uppercase text-[var(--text-tertiary)]">Working days</p>
          <p className="mt-1 text-metric tabular text-[var(--text-primary)]">{working.length}</p>
        </Card>
        <Card>
          <p className="text-caption uppercase text-[var(--text-tertiary)]">{enumLabel('NIGHT', locale)}</p>
          <p className="mt-1 text-metric tabular text-ai-plasma-text dark:text-ai-plasma-lift">{nights}</p>
        </Card>
        <Card>
          <p className="text-caption uppercase text-[var(--text-tertiary)]">{enumLabel('OFF', locale)}</p>
          <p className="mt-1 text-metric tabular text-[var(--text-secondary)]">
            {(data?.length ?? 0) - working.length}
          </p>
        </Card>
      </div>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.length ? (
          <EmptyState icon={<CalendarClock className="h-8 w-8" />} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('common.date')}</Th>
                <Th>{t('shifts.definitions')}</Th>
                <Th>{t('shifts.startTime')}</Th>
                <Th>{t('shifts.endTime')}</Th>
                <Th align="end">{t('common.notes')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((day) => {
                const dateString = day.date.slice(0, 10);
                const isToday = dateString === todayString;
                // A night shift's end lands on the next calendar day (§15.3).
                const crossesMidnight =
                  day.startAt && day.endAt && new Date(day.endAt).getUTCDate() !== new Date(day.startAt).getUTCDate();

                return (
                  <Tr key={day.id} className={clsx(isToday && 'bg-primary/6')}>
                    <Td>
                      <span className="flex items-center gap-2">
                        <span className="tabular">{formatDate(dateString, locale)}</span>
                        {isToday && <Badge tone="brand">Today</Badge>}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={TYPE_TONE[day.type]}>{enumLabel(day.type, locale)}</Badge>
                      {day.shiftDefinition && (
                        <span className="ms-2 text-caption text-muted">
                          {locale === 'ar' ? day.shiftDefinition.nameAr ?? day.shiftDefinition.name : day.shiftDefinition.name}
                        </span>
                      )}
                    </Td>
                    <Td className="tabular text-muted">{day.startAt ? formatTime(day.startAt, locale) : '—'}</Td>
                    <Td className="tabular text-muted">
                      {day.endAt ? formatTime(day.endAt, locale) : '—'}
                      {crossesMidnight && (
                        <span className="ms-1.5 text-caption text-ai-plasma-text dark:text-ai-plasma-lift">+1</span>
                      )}
                    </Td>
                    <Td align="end" className="text-caption text-faint">{day.notes ?? '—'}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
